import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type JSX,
  type ReactNode,
} from 'react';
import { AUTH_REQUIRED_EVENT, getToken, setToken } from './token';
import {
  ApiError,
  forgotPassword,
  getConfig,
  login,
  loginMfa,
  register,
  resendVerification,
  resetPassword,
  verifyEmail,
} from '../api/client';
import { useI18n } from '../i18n';
import type { LoginResponse } from '../api/types';

// Auth-Schicht (Issue #191): das Backend erzwingt Bearer-Auth. Ohne (gültigen) Token zeigt die App
// einen Anmelde-Screen; danach hängt der API-Client den Token an jeden Request. Bei 401 verwirft
// der Client den Token und feuert AUTH_REQUIRED_EVENT → wir zeigen den Screen erneut.
//
// Der Screen deckt die durch die gemergten Endpunkte (#186..#189, #197) gedeckten Abläufe ab:
// Anmelden (status-bewusste Fehler, keine Enumeration), Registrieren (nur wenn das Backend die
// Selbstregistrierung anbietet), E-Mail bestätigen, Passwort vergessen und zurücksetzen. Der
// direkte Token-Weg (Bestands-/statische Tokens) bleibt erhalten.
//
// Landing-Erkennung: die Mail-Links tragen `identity`/`token` in der URL. Ist ein `token`
// vorhanden, öffnet der Screen den passenden Modus (reset, wenn zusätzlich eine identity da ist —
// der Reset-Endpunkt braucht beide; sonst verify). Ein optionaler `?flow=`-Hinweis übersteuert die
// Heuristik. Nach Abschluss räumen wir die Query wieder auf, damit ein Reload nicht erneut landet.

type Mode = 'password' | 'token' | 'register' | 'forgot' | 'reset' | 'verify' | 'mfa';

// Gemeinsame Klassen der Eingabefelder — deckungsgleich mit den Panel-Feldern (Navy-Fläche,
// CI-Border, Gold-Fokus). So bleibt der Auth-Screen mit den restlichen Ansichten im CI.
const INPUT_CLASS =
  'w-full rounded border border-bf-border bg-bf-navy px-3 py-2.5 text-sm text-bf-white ' +
  'placeholder:text-bf-muted outline-none focus:border-bf-gold';

const PRIMARY_BTN =
  'mt-3.5 w-full rounded bg-bf-gold px-3 py-2.5 text-sm font-semibold text-bf-navy transition-colors ' +
  'hover:bg-bf-gold-light disabled:cursor-not-allowed disabled:opacity-60';

const LINK_BTN =
  'mt-2.5 w-full rounded border border-bf-border bg-transparent px-3 py-2 text-sm text-bf-muted ' +
  'transition-colors hover:border-bf-gold hover:text-bf-white';

// Verdichtet einen Login-Fehler zu einer klaren, für den legitimen Nutzer brauchbaren Meldung —
// ohne Enumeration: 401 bleibt immer die generische Meldung, erst nach korrektem Passwort verrät
// das Backend per 403-`code` den Status (email_unverified/approval_pending/account_rejected/…).
function loginErrorKey(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return 'login.error.too_many_attempts';
    if (err.status === 403) {
      switch (err.code) {
        case 'email_unverified':
          return 'login.error.email_unverified';
        case 'approval_pending':
          return 'login.error.approval_pending';
        case 'account_rejected':
          return 'login.error.account_rejected';
        case 'account_disabled':
          return 'login.error.account_disabled';
        default:
          return 'login.error.account_not_active';
      }
    }
  }
  // 401, Netzfehler und alles Übrige: eine generische Meldung (keine Enumeration).
  return 'login.failed';
}

// Query-Parameter der aktuellen URL lesen (Landing aus dem Mail-Link).
function readLanding(): { mode: Mode | null; identity: string; token: string } {
  if (typeof window === 'undefined') return { mode: null, identity: '', token: '' };
  const params = new URLSearchParams(window.location.search);
  const identity = params.get('identity') ?? '';
  const token = params.get('token') ?? '';
  const flow = params.get('flow');
  if (flow === 'reset') return { mode: 'reset', identity, token };
  if (flow === 'verify') return { mode: 'verify', identity, token };
  if (token) {
    // Reset braucht identity + token; verify kommt auch nur mit token aus.
    return { mode: identity ? 'reset' : 'verify', identity, token };
  }
  return { mode: null, identity, token };
}

// Die Landing-Query aus der Adresszeile entfernen (ohne Reload), damit ein erneutes Laden nicht
// wieder in den Verify-/Reset-Modus fällt.
function clearLandingQuery(): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  const url = window.location.pathname + window.location.hash;
  window.history.replaceState(null, '', url || '/');
}

export function LoginGate({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useI18n();
  const landing = useMemo(readLanding, []);
  const [authed, setAuthed] = useState<boolean>(() => Boolean(getToken()));
  const [mode, setMode] = useState<Mode>(landing.mode ?? 'password');
  const [selfRegAllowed, setSelfRegAllowed] = useState(false);

  // Login/Token
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [tokenValue, setTokenValue] = useState('');

  // Registrieren
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regName, setRegName] = useState('');

  // Passwort vergessen / zurücksetzen
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetIdentity, setResetIdentity] = useState(landing.identity);
  const [resetToken, setResetToken] = useState(landing.token);
  const [resetPw, setResetPw] = useState('');

  // MFA-Zweitschritt (Issue #192): das Ticket aus Schritt 1 + der Authenticator-/Recovery-Code.
  const [mfaTicket, setMfaTicket] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaUseRecovery, setMfaUseRecovery] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onRequired = () => setAuthed(false);
    window.addEventListener(AUTH_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, onRequired);
  }, []);

  // Ob das Backend die Selbstregistrierung anbietet — steuert die Sichtbarkeit des Registrieren-
  // Wegs. Der Endpunkt selbst bleibt fail-closed; das Flag ist nur die Anzeige.
  useEffect(() => {
    let cancelled = false;
    getConfig()
      .then((cfg) => !cancelled && setSelfRegAllowed(Boolean(cfg.allowSelfRegistration)))
      .catch(() => {
        /* Config nicht lesbar → Registrieren vorsichtshalber ausblenden */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Wenn wir per Verify-Link gelandet sind: die Bestätigung direkt ausführen (kein extra Klick).
  useEffect(() => {
    if (landing.mode !== 'verify') return;
    if (!landing.token) {
      setError(t('verify.missing'));
      return;
    }
    let cancelled = false;
    setBusy(true);
    verifyEmail(landing.identity, landing.token)
      .then((res) => {
        if (cancelled) return;
        setInfo(res.message || t('verify.done'));
        clearLandingQuery();
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError && err.status === 404
            ? t('register.disabled')
            : t('verify.failed'),
        );
      })
      .finally(() => !cancelled && setBusy(false));
    return () => {
      cancelled = true;
    };
  }, [landing.mode, landing.identity, landing.token, t]);

  const switchTo = (m: Mode) => {
    setMode(m);
    setError(null);
    setInfo(null);
  };

  // Ein erfolgreiches Login-Ergebnis abschließen: Token setzen und die App freigeben. Trägt die
  // Antwort stattdessen eine MFA-Forderung (Issue #192), wird in den mfa-Modus gewechselt (kein
  // Token, sondern das Ticket für den Zweitschritt).
  const finishLogin = useCallback((res: LoginResponse): boolean => {
    if (res.mfaRequired) {
      setMfaTicket(res.mfaTicket ?? '');
      setMfaCode('');
      setMfaUseRecovery(false);
      setMode('mfa');
      setError(null);
      setInfo(null);
      return false;
    }
    if (res.token) {
      setToken(res.token);
      clearLandingQuery();
      setAuthed(true);
      return true;
    }
    return false;
  }, []);

  const submitPassword = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const u = username.trim();
      if (!u || !password) return;
      setBusy(true);
      setError(null);
      setInfo(null);
      try {
        const res = await login(u, password);
        const done = finishLogin(res);
        if (done || res.mfaRequired) {
          setPassword('');
        }
        if (done) setUsername('');
      } catch (err) {
        setError(t(loginErrorKey(err)));
      } finally {
        setBusy(false);
      }
    },
    [username, password, t, finishLogin],
  );

  const submitMfa = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const code = mfaCode.trim();
      if (!mfaTicket || !code) return;
      setBusy(true);
      setError(null);
      setInfo(null);
      try {
        const res = await loginMfa(
          mfaUseRecovery
            ? { ticket: mfaTicket, recoveryCode: code }
            : { ticket: mfaTicket, code },
        );
        const done = finishLogin(res);
        if (done) {
          setUsername('');
          setMfaTicket('');
          setMfaCode('');
        }
      } catch (err) {
        // Das Backend liefert für falschen Code UND abgelaufenes/verbrauchtes Ticket denselben 401
        // (keine Enumeration). Wir zeigen eine generische Meldung und bleiben im mfa-Modus; über den
        // „Zurück"-Weg kommt der Nutzer bei einem abgelaufenen Ticket wieder zum Passwort-Login.
        setError(err instanceof ApiError ? t('mfa.error.invalid') : t('login.failed'));
        setMfaCode('');
      } finally {
        setBusy(false);
      }
    },
    [mfaTicket, mfaCode, mfaUseRecovery, t, finishLogin],
  );

  const submitToken = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const tok = tokenValue.trim();
      if (!tok) return;
      setToken(tok);
      setTokenValue('');
      clearLandingQuery();
      setAuthed(true);
    },
    [tokenValue],
  );

  const submitRegister = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const email = regEmail.trim();
      if (!email || !regPassword) return;
      setBusy(true);
      setError(null);
      setInfo(null);
      try {
        const res = await register({
          email,
          password: regPassword,
          displayName: regName.trim() || undefined,
        });
        setInfo(res.message || t('register.submitted'));
        setRegPassword('');
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          setError(t('register.conflict'));
        } else if (err instanceof ApiError && err.status === 404) {
          setError(t('register.disabled'));
        } else if (err instanceof ApiError && err.status === 422) {
          setError(t('register.invalid'));
        } else {
          setError(t('register.failed'));
        }
      } finally {
        setBusy(false);
      }
    },
    [regEmail, regPassword, regName, t],
  );

  const submitForgot = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const email = forgotEmail.trim();
      if (!email) return;
      setBusy(true);
      setError(null);
      setInfo(null);
      try {
        const res = await forgotPassword(email);
        // Neutrale Bestätigung — bewusst identisch, egal ob die Adresse existiert.
        setInfo(res.message || t('forgot.sent'));
      } catch (err) {
        if (err instanceof ApiError && err.status === 429) {
          setError(t('forgot.tooMany'));
        } else {
          setError(t('forgot.failed'));
        }
      } finally {
        setBusy(false);
      }
    },
    [forgotEmail, t],
  );

  const submitReset = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const id = resetIdentity.trim();
      const tok = resetToken.trim();
      if (!id || !tok || !resetPw) return;
      setBusy(true);
      setError(null);
      setInfo(null);
      try {
        const res = await resetPassword(id, tok, resetPw);
        setResetPw('');
        clearLandingQuery();
        // Nach erfolgreichem Reset zurück zum Login, dort die Bestätigung zeigen (neu anmelden).
        switchTo('password');
        setInfo(res.message || t('reset.done'));
      } catch (err) {
        setError(
          err instanceof ApiError && err.status === 400 ? t('reset.invalid') : t('reset.failed'),
        );
      } finally {
        setBusy(false);
      }
    },
    [resetIdentity, resetToken, resetPw, t],
  );

  const submitResend = useCallback(async () => {
    const id = resetIdentity.trim() || username.trim() || regEmail.trim();
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      const res = await resendVerification(id);
      setInfo(res.message || t('verify.resent'));
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? t('verify.resendTooMany')
          : t('verify.failed'),
      );
    } finally {
      setBusy(false);
    }
  }, [resetIdentity, username, regEmail, t]);

  if (authed) return <>{children}</>;

  const onSubmit =
    mode === 'password'
      ? submitPassword
      : mode === 'token'
        ? submitToken
        : mode === 'register'
          ? submitRegister
          : mode === 'forgot'
            ? submitForgot
            : mode === 'reset'
              ? submitReset
              : mode === 'mfa'
                ? submitMfa
                : (e: FormEvent) => e.preventDefault();

  const subtitleKey =
    mode === 'password'
      ? 'login.subtitlePassword'
      : mode === 'token'
        ? 'login.subtitleToken'
        : mode === 'register'
          ? 'register.subtitle'
          : mode === 'forgot'
            ? 'forgot.subtitle'
            : mode === 'reset'
              ? 'reset.subtitle'
              : mode === 'mfa'
                ? 'mfa.subtitle'
                : 'verify.subtitle';

  const submitLabelKey =
    mode === 'register'
      ? 'register.submit'
      : mode === 'forgot'
        ? 'forgot.submit'
        : mode === 'reset'
          ? 'reset.submit'
          : mode === 'mfa'
            ? 'mfa.submit'
            : 'login.submit';

  return (
    <div className="flex min-h-screen items-center justify-center bg-bf-navy p-4 text-bf-white">
      <form
        onSubmit={onSubmit}
        className="w-[380px] max-w-[90vw] rounded-xl border border-bf-border bg-bf-navy-light p-7"
      >
        <h1 className="mb-1 text-xl font-bold text-bf-white">{t('login.title')}</h1>
        <p className="mb-5 text-sm text-bf-muted">{t(subtitleKey)}</p>

        {mode === 'password' && (
          <div className="flex flex-col gap-2.5">
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('login.username')}
              autoFocus
              aria-label={t('login.username')}
              autoComplete="username"
              className={INPUT_CLASS}
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('login.password')}
              aria-label={t('login.password')}
              autoComplete="current-password"
              className={INPUT_CLASS}
            />
          </div>
        )}

        {mode === 'token' && (
          <input
            type="password"
            value={tokenValue}
            onChange={(e) => setTokenValue(e.target.value)}
            placeholder={t('login.token')}
            autoFocus
            aria-label={t('login.token')}
            className={INPUT_CLASS}
          />
        )}

        {mode === 'register' && (
          <div className="flex flex-col gap-2.5">
            <input
              type="email"
              value={regEmail}
              onChange={(e) => setRegEmail(e.target.value)}
              placeholder={t('register.email')}
              autoFocus
              aria-label={t('register.email')}
              autoComplete="email"
              className={INPUT_CLASS}
            />
            <input
              type="password"
              value={regPassword}
              onChange={(e) => setRegPassword(e.target.value)}
              placeholder={t('register.password')}
              aria-label={t('register.password')}
              autoComplete="new-password"
              className={INPUT_CLASS}
            />
            <input
              type="text"
              value={regName}
              onChange={(e) => setRegName(e.target.value)}
              placeholder={t('register.name')}
              aria-label={t('register.name')}
              autoComplete="name"
              className={INPUT_CLASS}
            />
          </div>
        )}

        {mode === 'forgot' && (
          <input
            type="email"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            placeholder={t('forgot.email')}
            autoFocus
            aria-label={t('forgot.email')}
            autoComplete="email"
            className={INPUT_CLASS}
          />
        )}

        {mode === 'reset' && (
          <div className="flex flex-col gap-2.5">
            <input
              type="text"
              value={resetIdentity}
              onChange={(e) => setResetIdentity(e.target.value)}
              placeholder={t('reset.identity')}
              aria-label={t('reset.identity')}
              autoComplete="username"
              className={INPUT_CLASS}
            />
            <input
              type="text"
              value={resetToken}
              onChange={(e) => setResetToken(e.target.value)}
              placeholder={t('reset.token')}
              aria-label={t('reset.token')}
              className={INPUT_CLASS}
            />
            <input
              type="password"
              value={resetPw}
              onChange={(e) => setResetPw(e.target.value)}
              placeholder={t('reset.newPassword')}
              aria-label={t('reset.newPassword')}
              autoComplete="new-password"
              className={INPUT_CLASS}
            />
          </div>
        )}

        {mode === 'mfa' && (
          <div className="flex flex-col gap-2.5">
            <p className="text-sm text-bf-muted">
              {mfaUseRecovery ? t('mfa.recoveryHint') : t('mfa.hint')}
            </p>
            <input
              type="text"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              placeholder={mfaUseRecovery ? t('mfa.recoveryPlaceholder') : t('mfa.codePlaceholder')}
              autoFocus
              inputMode={mfaUseRecovery ? 'text' : 'numeric'}
              aria-label={mfaUseRecovery ? t('mfa.recoveryPlaceholder') : t('mfa.codePlaceholder')}
              autoComplete="one-time-code"
              className={INPUT_CLASS}
            />
          </div>
        )}

        {mode === 'verify' && (
          <p className="text-sm text-bf-muted">{busy ? t('verify.checking') : t('verify.hint')}</p>
        )}

        {error && (
          <p role="alert" className="mt-2.5 text-sm text-red-300">
            {error}
          </p>
        )}
        {info && (
          <p role="status" className="mt-2.5 text-sm text-bf-gold">
            {info}
          </p>
        )}

        {mode !== 'verify' && (
          <button type="submit" disabled={busy} className={PRIMARY_BTN}>
            {t(submitLabelKey)}
          </button>
        )}

        {/* Kontextabhängige Wege zwischen den Modi. */}
        {mode === 'password' && (
          <>
            <button type="button" onClick={() => switchTo('forgot')} className={LINK_BTN}>
              {t('login.forgot')}
            </button>
            {selfRegAllowed && (
              <button type="button" onClick={() => switchTo('register')} className={LINK_BTN}>
                {t('login.toRegister')}
              </button>
            )}
            <button type="button" onClick={() => switchTo('token')} className={LINK_BTN}>
              {t('login.withToken')}
            </button>
          </>
        )}

        {mode === 'token' && (
          <button type="button" onClick={() => switchTo('password')} className={LINK_BTN}>
            {t('login.withPassword')}
          </button>
        )}

        {(mode === 'register' || mode === 'forgot' || mode === 'reset') && (
          <button type="button" onClick={() => switchTo('password')} className={LINK_BTN}>
            {t('auth.backToLogin')}
          </button>
        )}

        {mode === 'verify' && (
          <>
            <button type="button" onClick={submitResend} disabled={busy} className={LINK_BTN}>
              {t('verify.resend')}
            </button>
            <button type="button" onClick={() => switchTo('password')} className={LINK_BTN}>
              {t('auth.backToLogin')}
            </button>
          </>
        )}

        {mode === 'mfa' && (
          <>
            <button
              type="button"
              onClick={() => {
                setMfaUseRecovery((v) => !v);
                setMfaCode('');
                setError(null);
              }}
              className={LINK_BTN}
            >
              {mfaUseRecovery ? t('mfa.useAuthenticator') : t('mfa.useRecovery')}
            </button>
            <button
              type="button"
              onClick={() => {
                setMfaTicket('');
                setMfaCode('');
                switchTo('password');
              }}
              className={LINK_BTN}
            >
              {t('auth.backToLogin')}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
